import { CustomEventWithDetail, CustomEventWithoutDetail, html, rune, View } from 'rune-ts';
import klass from '../ProductList.module.scss';
import { CloseIcon, FilterIcon } from '../../../components/atoms/Icon';
import { staticTypo, typo } from '../../../../shared/typography/typo';
import { CreatorFilter, ProductFilterOption, StyleFilter } from '../../../../features/ProductList/type';
import { getCurrency } from '../../../../../../modules/Util/S/Function/util';
import { htmlIf } from '../../../../shared/util';
import { isNumber } from '@fxts/core';
import { getMaxPriceByLanguage } from '../../../../features/ProductList/util';

export const PRODUCT_FILTER_ID = {
  PRICE: 'PRICE',
  ONLY_LIMITED: 'ONLY_LIMITED',
  WITHOUT_SOLD_OUT: 'WITHOUT_SOLD_OUT',
  CREATOR: 'CREATOR',
  STYLE: 'STYLE',
} as const;

type RemoveButtonData<T = any> = {
  id: keyof typeof PRODUCT_FILTER_ID;
  name: string;
  value?: T;
};

export class ProductFilterResetEvent extends CustomEventWithoutDetail {}

export class ButtonRemovedEvent extends CustomEventWithDetail<RemoveButtonData> {}

class RemoveButton extends View<RemoveButtonData> {
  override template() {
    return html`<span class="${klass.remove} ${typo('14_medium')}">
      ${this.data.name}
      <button class="${klass.icon} remove">${CloseIcon()}</button>
    </span>`;
  }

  remove() {
    this.element().remove();
  }
}

export class ProductFilterToggleButton extends View<ProductFilterOption> {
  creator_filter_map: Map<number, string>;
  style_filter_map: Map<number, string>;

  MIN_PRICE: Readonly<number>;
  MAX_PRICE: Readonly<number>;

  HIDDEN_KLASS: Readonly<string> = klass.hidden;

  constructor(
    data: ProductFilterOption,
    filter: {
      creator_filters: CreatorFilter[];
      style_filters: StyleFilter[];
    },
  ) {
    super(data);
    this.creator_filter_map = new Map(filter.creator_filters.map((f) => [f.id, f.name]));
    this.style_filter_map = new Map(filter.style_filters.map((f) => [f.id, f.name]));
    this.MIN_PRICE = 0;
    this.MAX_PRICE = getMaxPriceByLanguage(ET.lang);
  }

  override template() {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    if (is_mobile) {
      return html`
        <div class="${klass.filter_btn_container} ${htmlIf(klass.active, !this.isInitialState())}">
          <button class="${klass.toggle}">
            <span class="${klass.icon}">${FilterIcon}</span>
          </button>
        </div>
      `;
    }

    const { min_price, max_price, creator_filter_ids = [], style_filter_ids = [] } = this.data;

    const hidden_klass = htmlIf(this.HIDDEN_KLASS, this.isInitialState());
    const price_label = makePriceLabel(min_price, max_price);

    return html`
      <div class="${klass.filter_btn_container}">
        <button class="${klass.toggle}">
          <span class="${klass.icon}">${FilterIcon}</span>
          <span class="${staticTypo('unica_16_medium')}">Filter</span>
        </button>

        <div class="${klass.remove_container} ${hidden_klass}">
          ${price_label ? new RemoveButton({ name: price_label, id: PRODUCT_FILTER_ID.PRICE }) : ''}
          ${creator_filter_ids.map((id) => {
            return new RemoveButton({
              name: this.creator_filter_map.get(id),
              id: PRODUCT_FILTER_ID.CREATOR,
              value: id,
            });
          })}
          ${style_filter_ids.map((id) => {
            return new RemoveButton({
              name: this.style_filter_map.get(id)!,
              id: PRODUCT_FILTER_ID.STYLE,
              value: id,
            });
          })}
        </div>

        <button class="${klass.reset} ${hidden_klass} ${typo('14_medium')}">
          ${ET('mps2::common::reset')}
        </button>
      </div>
    `;
  }

  override onRender() {
    this.delegate('click', RemoveButton, (e, view) => {
      view.remove();
      this.dispatchEvent(ButtonRemovedEvent, {
        bubbles: true,
        detail: view.data,
      });
    });

    this.delegate('click', `.${klass.reset}`, (e) => {
      this.subViews(RemoveButton).forEach((view) => view.remove());
      this.dispatchEvent(ProductFilterResetEvent, { bubbles: true });
    });
  }

  setData(data: ProductFilterOption) {
    this.data.max_price = data.max_price;
    this.data.min_price = data.min_price;
    this.data.creator_filter_ids = data.creator_filter_ids;
    this.data.style_filter_ids = data.style_filter_ids;
  }

  remove(id: keyof typeof PRODUCT_FILTER_ID, value?: number) {
    if (id === PRODUCT_FILTER_ID.PRICE) {
      this.data.min_price = undefined;
      this.data.max_price = undefined;
    } else if (id === PRODUCT_FILTER_ID.CREATOR) {
      removeItems(this.data.creator_filter_ids ?? [], value);
    } else if (id === PRODUCT_FILTER_ID.STYLE) {
      removeItems(this.data.style_filter_ids ?? [], value);
    }
    return this.data;
  }

  /**
   * 필터가 아무것도 선택되지 않은 상태이면 `true`
   */
  private isInitialState(): boolean {
    const { min_price, max_price, creator_filter_ids = [], style_filter_ids = [] } = this.data;

    return !(min_price || max_price || creator_filter_ids.length || style_filter_ids.length);
  }
}

const makePriceLabel = (min_price?: number, max_price?: number): string | undefined => {
  const min = getMinPrice(min_price);
  const max = getMaxPrice(max_price);
  const currency = getCurrency(ET.lang);

  if (min && max) {
    if (ET.lang === 'kr') {
      return `${min.toLocaleString()}${currency} ~ ${max.toLocaleString()}${currency}`;
    }
    return `${currency}${min.toLocaleString()} ~ ${currency}${max.toLocaleString()}`;
  }
  if (min) {
    if (ET.lang === 'kr') {
      return `${min.toLocaleString()}${currency} ~ `;
    }
    return `${currency}${min.toLocaleString()} ~ `;
  }
  if (max) {
    if (ET.lang === 'kr') {
      return `~ ${max.toLocaleString()}${currency}`;
    }
    return `~ ${currency}${max.toLocaleString()}`;
  }
  return undefined;
};

const getMinPrice = (min_price?: number): number | undefined => {
  const MIN = 0;
  const MAX = getMaxPriceByLanguage(ET.lang);

  if (!isNumber(min_price)) {
    return undefined;
  }
  if (min_price >= MIN && min_price <= MAX - 1) {
    return min_price;
  }
  return undefined;
};

const getMaxPrice = (max_price?: number): number | undefined => {
  const MIN = 0;
  const MAX = getMaxPriceByLanguage(ET.lang);

  if (!isNumber(max_price)) {
    return undefined;
  }
  if (max_price >= MIN && max_price <= MAX) {
    return max_price;
  }
  return undefined;
};

const removeItems = <T>(arr: T[], value: T): T[] => {
  let i = 0;
  while (i < arr.length) {
    if (arr[i] === value) {
      arr.splice(i, 1);
    } else {
      ++i;
    }
  }
  return arr;
};
